import React from "react";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import TeamTabs from "@/modules/hub/features/EmployeeDirectory/components/TeamTabs/TeamTabs";
import SpecialtyFilters from "@/modules/hub/features/EmployeeDirectory/components/SpecialtyFilters/SpecialtyFilters";
import EmployeeProfileCard from "@/modules/hub/features/EmployeeDirectory/components/EmployeeProfileCard/EmployeeProfileCard";
import { useEmployeeDirectoryPage } from "@/modules/hub/features/EmployeeDirectory/hooks/useEmployeeDirectoryPage";

const HERO_SUBTITLE =
  "Running a home services business can feel overwhelming. Whether you're just starting an HVAC company or decades into an electrical contracting career, the challenges of growing a profitable business while maintaining your sanity are real. CertainPath works exclusively with HVAC, plumbing, electrical, and roofing contractors, and we've been in your shoes. Our professional coaching isn't one-size-fits-all fluff – it's tailored guidance for where you are in your journey.";

const EmployeeDirectoryPage: React.FC = () => {
  const {
    loading,
    companyName,
    filteredMembers,
    teams,
    totalProfileCount,
    activeTeamUuid,
    setActiveTeamUuid,
    specialtyOptions,
    selectedSpecialtyUuids,
    toggleSpecialty,
  } = useEmployeeDirectoryPage();

  const displayCompanyName = companyName || "Our";

  if (loading) {
    return (
      <div className="py-20">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 min-h-full">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-12 pb-0">
        <h1 className="text-center text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
          Meet the <span className="text-primary">{displayCompanyName}</span>{" "}
          Team
        </h1>
        <p className="mt-6 mx-auto max-w-3xl text-center text-base text-gray-600 dark:text-gray-300">
          {HERO_SUBTITLE}
        </p>

        <div className="mt-10 flex justify-center">
          <TeamTabs
            activeTeamUuid={activeTeamUuid}
            onChange={setActiveTeamUuid}
            teams={teams}
            totalCount={totalProfileCount}
          />
        </div>
      </div>

      <div className="border-y border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SpecialtyFilters
            onToggle={toggleSpecialty}
            options={specialtyOptions}
            selectedUuids={selectedSpecialtyUuids}
          />
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-widest text-primary uppercase">
            {displayCompanyName} Team
          </p>
          <h2 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
            Meet Our Team
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {filteredMembers.length} member
            {filteredMembers.length === 1 ? "" : "s"}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredMembers.map((member) => (
            <EmployeeProfileCard key={member.uuid} member={member} />
          ))}
        </div>

        {filteredMembers.length === 0 && (
          <div className="mt-10 text-center text-sm text-gray-500 dark:text-gray-400">
            No team members match the current filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDirectoryPage;
